<script setup>
import { computed, reactive } from "vue";
import { useRouter } from "vue-router";
import { useMainStore } from "@/stores/main";
import { useStyleStore } from "@/stores/style.js";
import { Auth } from "@/services";
import { mdiAlertCircle, mdiClose } from "@mdi/js";
import BaseIcon from "@/components/BaseIcon.vue";
import { useEscapeToClose } from "@/composables/useEscapeToClose";

const mainStore = useMainStore();
const styleStore = useStyleStore();
const router = useRouter();

// Teleported to <body>, outside .bt-app — needs its own themed token wrapper.
const theme = computed(() => (styleStore.darkMode ? "dark" : "light"));

const form = reactive({
  email: "",
  password: "",
  loading: false,
  error: null,
});

const close = () => {
  mainStore.demoModalVisible = false;
  form.email = "";
  form.password = "";
  form.error = null;
};

useEscapeToClose(
  computed(() => mainStore.demoModalVisible),
  close,
);

const signup = async () => {
  form.loading = true;
  form.error = null;
  try {
    await Auth.signup(form.email, form.password);
    mainStore.demoModalVisible = false;
    // Exit demo session; the new user will go through activation
    await Auth.logout();
    mainStore.setUser(null);
    mainStore.setOrganisation(null);
    router.push({ name: "awaiting-activation" });
  } catch (err) {
    form.error =
      err.response?.data?.message ??
      "Could not create account. Please try again.";
  } finally {
    form.loading = false;
  }
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="mainStore.demoModalVisible"
      class="bt-portal"
      :data-theme="theme"
    >
      <div class="bt-nudge-backdrop" @click.self="close">
        <div
          class="bt-nudge-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="nudge-title"
        >
          <button
            class="bt-nudge-close"
            type="button"
            :aria-label="'Close'"
            @click="close"
          >
            <BaseIcon :path="mdiClose" :size="18" w="w-auto" />
          </button>

          <div class="bt-nudge-icon" aria-hidden="true">🚀</div>
          <h2 id="nudge-title" class="bt-nudge-title">
            This feature requires an account
          </h2>
          <p class="bt-nudge-copy">
            Sign up free to manage your review channels, add topics, invite
            teammates, and more.
          </p>

          <form class="bt-nudge-form" @submit.prevent="signup">
            <div v-if="form.error" class="bt-nudge-error">
              <BaseIcon :path="mdiAlertCircle" :size="15" w="w-auto" />
              <span>{{ form.error }}</span>
            </div>

            <label class="bt-nudge-field">
              <span>Email</span>
              <input
                v-model="form.email"
                type="email"
                placeholder="you@company.com"
                autocomplete="username"
                required
              />
            </label>

            <label class="bt-nudge-field">
              <span>Password</span>
              <input
                v-model="form.password"
                type="password"
                placeholder="Min. 8 characters"
                autocomplete="new-password"
                minlength="8"
                required
              />
            </label>

            <button
              class="bt-btn bt-btn-primary"
              type="submit"
              :disabled="form.loading"
              style="width: 100%; justify-content: center"
            >
              {{ form.loading ? "Creating account…" : "Get Started" }}
            </button>
          </form>

          <p class="bt-nudge-maybe">
            <button type="button" class="bt-nudge-maybe-btn" @click="close">
              Maybe later
            </button>
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.bt-nudge-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  z-index: 500;
  padding: 20px;
}

.bt-nudge-modal {
  position: relative;
  width: 100%;
  max-width: 400px;
  padding: 28px 24px 22px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-lg, 0 20px 60px rgba(0, 0, 0, 0.25));
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.bt-nudge-close {
  position: absolute;
  top: 14px;
  right: 14px;
  padding: 4px;
  border: 0;
  background: transparent;
  color: var(--text-3);
  cursor: pointer;
  display: grid;
  place-items: center;
  border-radius: var(--r-sm);
}
.bt-nudge-close:hover {
  background: var(--surface-2);
  color: var(--text);
}

.bt-nudge-icon {
  font-size: 32px;
  line-height: 1;
}

.bt-nudge-title {
  margin: 0;
  font-size: 18px;
  font-weight: 850;
  color: var(--text);
}

.bt-nudge-copy {
  margin: 0;
  font-size: 13.5px;
  color: var(--text-2);
  line-height: 1.6;
}

.bt-nudge-error {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px solid var(--neg);
  border-radius: var(--r-sm);
  background: var(--neg-soft);
  color: var(--neg);
  font-size: 12.5px;
  font-weight: 700;
}

.bt-nudge-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bt-nudge-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.bt-nudge-field > span {
  font-size: 11.5px;
  font-weight: 800;
  color: var(--text);
}

.bt-nudge-field input {
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  background: var(--surface-2);
  color: var(--text);
  font: 700 13px/1 var(--ui);
  outline: none;
}
.bt-nudge-field input:focus {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-soft);
}
.bt-nudge-field input::placeholder {
  color: var(--text-3);
  font-weight: 400;
}

.bt-nudge-maybe {
  margin: 0;
  text-align: center;
}

.bt-nudge-maybe-btn {
  border: 0;
  background: transparent;
  color: var(--text-3);
  font-size: 12.5px;
  cursor: pointer;
  text-decoration: underline;
}
.bt-nudge-maybe-btn:hover {
  color: var(--text-2);
}
</style>
